import { useMemo, useState } from "react";
import { ScenarioSchema, type Scenario } from "@h2lca/core";
import { Button } from "@/components/ui/FormControls";
import { api } from "@/lib/api";
import { suggestImportMapping } from "@/components/workshop/import-mapping";
import type { ImportRecord } from "./import-template";

interface ImportApplyPanelProps {
  projectId: string;
  importId: string;
  records: ImportRecord[];
  scenario: Scenario | null;
  onApplied: (scenario: Scenario, updatedAt: string, appliedRows: number, skippedRows: number) => void;
}

export function ImportApplyPanel({ projectId, importId, records, scenario, onApplied }: ImportApplyPanelProps) {
  const mapping = useMemo(() => scenario ? suggestImportMapping(records, scenario) : { matches: [], unmatched: [] }, [records, scenario]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function apply() {
    if (!scenario || mapping.matches.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const response = await api<{ scenario: unknown; updatedAt?: string; application: { appliedRows: number; skippedRows: number } }>(`/workspace/projects/${projectId}/imports/${importId}/apply`, {
        method: "POST",
        json: { scenario, mappings: mapping.matches.map((match) => ({ rowIndex: match.rowIndex, equipmentId: match.equipmentId, inputId: match.inputId })) },
      });
      const parsed = ScenarioSchema.safeParse(response.scenario);
      if (!parsed.success) throw new Error("반영된 산정 시나리오를 읽을 수 없습니다.");
      onApplied(parsed.data, response.updatedAt ?? new Date().toISOString(), response.application.appliedRows, response.application.skippedRows);
    } catch (e) {
      setError(e instanceof Error ? e.message : "검증한 값을 산정 입력값에 반영하지 못했습니다.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="workspace-card workspace-import-apply">
      <div className="workspace-card-head">
        <div>
          <h2 className="workspace-card-title">산정 입력값 연결</h2>
          <p className="workspace-card-desc">검증된 행 중 설비명, 활동자료명, 단위가 일치하는 항목만 현재 산정에 반영합니다.</p>
        </div>
        <span className="badge-accent">기업용</span>
      </div>
      {!scenario ? (
        <p className="workspace-empty">현재 열려 있는 산정이 없어 연결 후보를 만들 수 없습니다. 산정 화면에서 시나리오를 먼저 열어 주세요.</p>
      ) : (
        <>
          <div className="workspace-import-match-summary">
            <span className="badge-accent">자동 연결 후보 {mapping.matches.length}개</span>
            <span className={mapping.unmatched.length > 0 ? "badge-review" : "badge-muted"}>확인 필요 {mapping.unmatched.length}개</span>
            <Button variant="primary" disabled={busy || mapping.matches.length === 0} onClick={() => void apply()}>{busy ? "반영 중" : "검증한 값을 산정에 반영"}</Button>
          </div>
          {mapping.unmatched.length > 0 && (
            <details className="workspace-unmatched-list">
              <summary>반영하지 않은 행 확인</summary>
              <ul>{mapping.unmatched.slice(0, 12).map((item, index) => <li key={`${item.record.activity}-${index}`}>{item.record.equipment || "설비 미지정"}, {item.record.activity || "활동자료 미지정"}: {item.reason}</li>)}</ul>
            </details>
          )}
        </>
      )}
      {error && <p className="note note-danger text-footnote" role="alert">{error}</p>}
    </section>
  );
}
